import React, { useState } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { formatEmployeeExperience } from '../utils/employee';

const LEVELS = [
  '',
  'Estagiária', 'Estagiário',
  'Arquiteta júnior', 'Arquiteto júnior',
  'Arquiteta pleno', 'Arquiteto pleno',
  'Arquiteta sênior', 'Arquiteto sênior',
];

export default function TeamEditor({ employee, order, onSave, onDelete, onSaved }) {
  const [name, setName] = useState(employee?.name || '');
  const [role, setRole] = useState(employee?.role || '');
  const [level, setLevel] = useState(employee?.experience_level || '');
  const [years, setYears] = useState(employee?.years_experience ?? '');
  const [photoUrl, setPhotoUrl] = useState(employee?.photo_url || '');
  const [employmentType, setEmploymentType] = useState(employee?.employment_type || 'permanent');
  const [isActive, setIsActive] = useState(employee?.is_active !== 0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch('/api/uploads', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Falha ao enviar a foto.');
      setPhotoUrl(data.url);
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      const savedEmployee = await onSave({
        id: employee?.id,
        name: name.trim(),
        role: role.trim(),
        // Keep legacy founder biography data intact; the aligned editor uses level and years.
        bio: employee?.bio || '',
        photo_url: photoUrl.trim(),
        employment_type: employmentType,
        experience_level: level,
        years_experience: years === '' ? null : Number(years),
        is_active: isActive,
        sort_order: order,
      });
      if (savedEmployee) onSaved(savedEmployee);
    } catch (saveError) {
      setError(saveError.message || 'Não foi possível salvar a pessoa.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!employee?.id || !onDelete) return;
    setError('');
    setSaving(true);
    try {
      await onDelete(employee.id);
    } catch (deleteError) {
      setError(deleteError.message || 'Não foi possível excluir a pessoa.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="admin-team-editor" onSubmit={handleSubmit}>
      <h2>{employee?.id ? `Editar ${employee.name}` : 'Adicionar pessoa'}</h2>

      <div className="admin-team-editor__photo-field">
        <div className="admin-team-editor__photo-preview">
          {photoUrl ? <img src={photoUrl} alt={`Foto de ${name || 'nova pessoa'}`} className="team-portrait" /> : <span>Sem foto</span>}
        </div>
        <div>
          <label className="btn btn--outline admin-team-editor__upload">
            <Upload size={15} /> {uploading ? 'Enviando...' : photoUrl ? 'Trocar foto' : 'Enviar foto'}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoUpload} disabled={uploading} />
          </label>
          <p className="admin-team-editor__hint">Use um retrato vertical. O site aplica o preto e branco e o recorte.</p>
        </div>
      </div>

      <label className="admin-team-editor__field">
        <span className="font-label">Nome</span>
        <input className="form-control" type="text" value={name} onChange={(event) => setName(event.target.value)} required maxLength={160} />
      </label>

      <label className="admin-team-editor__field">
        <span className="font-label">Função</span>
        <input className="form-control" type="text" value={role} onChange={(event) => setRole(event.target.value)} placeholder="Ex.: Equipe de desenvolvimento" />
      </label>

      <div className="admin-team-editor__row">
        <label className="admin-team-editor__field">
          <span className="font-label">Nível</span>
          <select className="form-control form-control--select" value={level} onChange={(event) => setLevel(event.target.value)}>
            {LEVELS.map((option) => <option key={option} value={option}>{option || 'Selecione o nível'}</option>)}
          </select>
        </label>
        <label className="admin-team-editor__field">
          <span className="font-label">Anos de atuação</span>
          <input className="form-control" type="number" min="0" max="60" value={years} onChange={(event) => setYears(event.target.value)} />
        </label>
      </div>

      <p className="admin-team-editor__hint">No card: <strong>{formatEmployeeExperience({ experience_level: level, years_experience: years }) || 'sem nível ou tempo de atuação'}</strong></p>

      <label className="admin-team-editor__field">
        <span className="font-label">Vínculo</span>
        <select className="form-control form-control--select" value={employmentType} onChange={(event) => setEmploymentType(event.target.value)}>
          <option value="permanent">Equipe permanente</option>
          <option value="temporary">Colaboradora por projeto</option>
        </select>
      </label>

      <label className="admin-team-editor__active">
        <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
        <span>Aparece no site</span>
      </label>

      {error && <p className="admin-team-editor__error" role="alert">{error}</p>}

      <div className="admin-team-editor__actions">
        {employee?.id && <button className="btn btn--outline btn--icon-danger" type="button" onClick={handleDelete} disabled={saving}><Trash2 size={14} /> Excluir pessoa</button>}
        <button className="btn btn--solid" type="submit" disabled={saving || uploading}>{saving ? 'Salvando...' : 'Salvar'}</button>
      </div>
    </form>
  );
}
